import type { IncomingHttpHeaders } from 'http';

import type { ModalRequiredProps } from 'components/Toolkit/Modal/BaseModal';
import type { InitialEnquiryModalValues } from 'types';
import type { ISearchPageAds } from 'api/types/searchPageApiTypes';
import type { VehicleLookUpData } from 'components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.config';

interface DealerEnquiryModalProps extends ModalRequiredProps {
  isUserLoggedIn: boolean;
  dealerName: string;
  dealerLogo?: string;
  enquiry?: InitialEnquiryModalValues;
  adId: number;
  authorizationHeader: IncomingHttpHeaders;
  sectionName?: string;
  setIsPostEnquirySimilarAdsModalOpen: (isOpen: boolean) => void;
  setSimilarAds: (similarAds: ISearchPageAds[]) => void;
}

interface UseDealerEnquiryModal {
  enquiry: InitialEnquiryModalValues;
  askToClose: () => void;
  includesDefaultMessageText: boolean;
  adId: number;
  sectionName?: string;
  authorizationHeader: IncomingHttpHeaders;
  resetModal: () => void;
  setIsSubmitting: (t: boolean) => void;
  setDisableSubmitButton: (t: boolean) => void;
  disableVehicleRegLookupButton: boolean;
  tradeInValues?: VehicleLookUpData;
  onLookupSuccess: (lookupData?: VehicleLookUpData) => void;
  onLookupFailure: (errorMessage: string) => void;
  populateModal: (enquiry?: InitialEnquiryModalValues) => void;
  setIsPostEnquirySimilarAdsModalOpen: (isOpen: boolean) => void;
  setSimilarAds: (recommendedAds: ISearchPageAds[]) => void;
}

enum ACTION_TYPE {
  SET_IS_SUBMITTING = 'SET_IS_SUBMITTING',
  SET_SHOW_KEY_INFO_LIST = 'SET_SHOW_KEY_INFO_LIST',
  SET_TRADE_IN_VALUES = 'SET_TRADE_IN_VALUES',
  SET_VEHICLE_LOOKUP_ERROR_MESSAGE = 'SET_VEHICLE_LOOKUP_ERROR_MESSAGE',
  SET_DISABLE_SUBMIT_BUTTON = 'SET_DISABLE_SUBMIT_BUTTON',
  SET_DISABLE_VEHICLE_REG_LOOKUP_BUTTON = 'SET_DISABLE_VEHICLE_REG_LOOKUP_BUTTON',
  RESET_MODAL = 'RESET_MODAL',
  ON_LOOKUP_SUCCESS = 'ON_LOOKUP_SUCCESS',
  ON_LOOKUP_FAILURE = 'ON_LOOKUP_FAILURE',
  POPULATE_MODAL = 'POPULATE_MODAL',
}

interface UseDealerEnquiryModalReducer {
  isSubmitting: boolean;
  disableSubmitButton: boolean;
  showKeyInfoList: boolean;
  disableVehicleRegLookupButton: boolean;
}

type SetIsSubmittingType = {
  type: ACTION_TYPE.SET_IS_SUBMITTING;
  payload: boolean;
};

type SetShowKeyInfoListType = {
  type: ACTION_TYPE.SET_SHOW_KEY_INFO_LIST;
  payload: boolean;
};

type SetTradeInValues = {
  type: ACTION_TYPE.SET_TRADE_IN_VALUES;
  payload?: VehicleLookUpData;
};

type SetVehicleLookUpErrorMessage = {
  type: ACTION_TYPE.SET_VEHICLE_LOOKUP_ERROR_MESSAGE;
  payload?: string;
};

type SetDisableSubmitButton = {
  type: ACTION_TYPE.SET_DISABLE_SUBMIT_BUTTON;
  payload: boolean;
};

type SetDisableVehicleRegLookupButton = {
  type: ACTION_TYPE.SET_DISABLE_VEHICLE_REG_LOOKUP_BUTTON;
  payload: boolean;
};

type ResetModal = {
  type: ACTION_TYPE.RESET_MODAL;
  payload: {
    isSubmitting: boolean;
    disableSubmitButton: boolean;
    showKeyInfoList: boolean;
    disableVehicleRegLookupButton: boolean;
  };
};

type OnLookupSuccess = {
  type: ACTION_TYPE.ON_LOOKUP_SUCCESS;
  payload?: VehicleLookUpData;
};

type OnLookupFailure = {
  type: ACTION_TYPE.ON_LOOKUP_FAILURE;
  payload: string;
};

type PopulateModal = {
  type: ACTION_TYPE.POPULATE_MODAL;
  payload?: InitialEnquiryModalValues;
};

type StateType = {
  isSubmitting: boolean;
  disableSubmitButton: boolean;
  showKeyInfoList: boolean;
  disableVehicleRegLookupButton: boolean;
  tradeInValues?: VehicleLookUpData;
  vehicleLookupErrorMessage?: string;
};

type ActionType =
  | SetIsSubmittingType
  | SetDisableSubmitButton
  | SetShowKeyInfoListType
  | SetTradeInValues
  | SetVehicleLookUpErrorMessage
  | SetDisableVehicleRegLookupButton
  | ResetModal
  | OnLookupSuccess
  | OnLookupFailure
  | PopulateModal;

export type {
  DealerEnquiryModalProps,
  UseDealerEnquiryModal,
  StateType,
  ActionType,
  UseDealerEnquiryModalReducer,
};
export { ACTION_TYPE };
